import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { REQUEST_TYPES } from "@/lib/crmConstants";
import { RequestTypeBadge, StageBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function RecordsListPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [userMap, setUserMap] = useState({});
  const [pipelines, setPipelines] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // "bulk" | recordObj | null
  const [busy, setBusy] = useState(false);

  const type = params.get("type") || "";
  const stage = params.get("stage") || "";
  const source = params.get("source") || "";

  const load = useCallback(async (searchTerm) => {
    setLoading(true);
    const qs = {};
    if (type) qs.request_type = type;
    if (stage) qs.stage = stage;
    if (source) qs.source = source;
    const term = searchTerm !== undefined ? searchTerm : q;
    if (term) qs.q = term;
    try {
      const r = await api.get("/records", { params: qs });
      setRecords(r.data);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, stage, source]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/meta/pipelines").then((r) => setPipelines(r.data.pipelines || {})).catch(() => setPipelines({}));
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "team_lead") {
      api.get("/users")
        .then((r) => {
          const map = {};
          (r.data || []).forEach((u) => { map[u.id] = u; });
          setUserMap(map);
        })
        .catch(() => setUserMap({}));
    }
  }, [user?.role]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    // Clear stage when type changes so we don't carry an incompatible stage.
    if (k === "type") p.delete("stage");
    setParams(p);
  };

  const stageOptions = useMemo(() => {
    if (type && pipelines[type]) return pipelines[type];
    const set = new Set();
    Object.values(pipelines).forEach((arr) => (arr || []).forEach((s) => set.add(s)));
    return Array.from(set);
  }, [type, pipelines]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === records.length) return new Set();
      return new Set(records.map((r) => r.id));
    });
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      if (confirmDelete === "bulk") {
        const ids = Array.from(selected);
        const { data } = await api.post("/records/bulk_delete", { ids });
        toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? "" : "s"}`);
      } else if (confirmDelete && confirmDelete.id) {
        await api.delete(`/records/${confirmDelete.id}`);
        toast.success(`Deleted ${confirmDelete.ticket_id}`);
      }
      setConfirmDelete(null);
      await load(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/export.csv${type ? `?request_type=${type}` : ""}`;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Records</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">
            {type ? REQUEST_TYPES[type]?.label || "Records" : "All records"}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(exportUrl, "_blank")} data-testid="records-export-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Link to="/new" data-testid="records-new-button">
            <Button size="sm" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
              <Plus className="w-4 h-4 mr-1" /> New record
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card-shell p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            data-testid="records-search-input"
            placeholder="Search by ticket, name, phone, email…"
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
          />
        </div>
        <Select value={type || "__all"} onValueChange={(v) => setParam("type", v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[220px]" data-testid="filter-request-type">
            <SelectValue placeholder="Request type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All types</SelectItem>
            {Object.entries(REQUEST_TYPES).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={stage || "__all"} onValueChange={(v) => setParam("stage", v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[200px]" data-testid="filter-stage" id="stage-filter">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All stages</SelectItem>
            {stageOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={source || "__all"} onValueChange={(v) => setParam("source", v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-source">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All sources</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => load(q)} data-testid="filter-apply">Apply</Button>
      </div>

      {/* Bulk actions bar — admin only, appears when rows are selected */}
      {isAdmin && selected.size > 0 && (
        <div
          className="card-shell p-3 flex items-center justify-between bg-[#FEF2F2] border-[#FCA5A5]"
          data-testid="bulk-action-bar"
        >
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span>{" "}
            record{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">
              Clear
            </Button>
            <Button
              size="sm"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              onClick={() => setConfirmDelete("bulk")}
              data-testid="bulk-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete {selected.size} record{selected.size === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-shell overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#71717A] text-sm">Loading…</div>
        ) : records.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[#71717A] text-sm">No records match your filters.</div>
            <Link to="/new" className="mt-3 inline-block text-[#4F46E5] font-medium hover:underline">
              Create a manual record →
            </Link>
          </div>
        ) : (
          <div className="scroll-shadow max-h-[calc(100vh-350px)] overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th style={{ width: 40 }}>
                      <Checkbox
                        checked={records.length > 0 && selected.size === records.length}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th>Ticket</th>
                  <th>Type</th>
                  <th>Contact</th>
                  <th>State</th>
                  <th>Stage</th>
                  <th>Source</th>
                  <th>Assigned to</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody data-testid="records-tbody">
                {records.map((r) => {
                  const d = r.data || {};
                  const name = d.full_name || d.contact_person || d.organisation_name || d.business_name || "—";
                  return (
                    <tr key={r.id} data-testid={`record-row-${r.ticket_id}`}>
                      {isAdmin && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(r.id)}
                            onCheckedChange={() => toggleRow(r.id)}
                            data-testid={`select-row-${r.ticket_id}`}
                          />
                        </td>
                      )}
                      <td className="font-mono text-[13px] font-medium">{r.ticket_id}</td>
                      <td><RequestTypeBadge requestType={r.request_type} /></td>
                      <td>
                        <div className="font-medium">{name}</div>
                        <div className="text-xs text-[#71717A]">{d.phone || d.email || ""}</div>
                      </td>
                      <td className="text-xs text-[#52525B]">{r.state || "—"}</td>
                      <td><StageBadge stage={r.stage} requestType={r.request_type} /></td>
                      <td className="text-xs uppercase tracking-wide text-[#71717A]">{r.source}</td>
                      <td className="text-xs text-[#52525B]">
                        {r.assigned_user_id
                          ? (userMap[r.assigned_user_id]?.name || <span className="font-mono">{r.assigned_user_id.slice(0, 8)}…</span>)
                          : <span className="text-[#A1A1AA] italic">shared queue</span>}
                      </td>
                      <td className="text-xs text-[#71717A]">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="whitespace-nowrap">
                        <Link
                          to={`/records/${r.id}`}
                          className="text-[#4F46E5] text-sm font-medium hover:underline mr-3"
                          data-testid={`open-record-${r.ticket_id}`}
                        >
                          Open →
                        </Link>
                        {isAdmin && (
                          <button
                            type="button"
                            className="text-[#DC2626] hover:text-[#991B1B]"
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }}
                            data-testid={`delete-record-${r.ticket_id}`}
                            aria-label={`Delete ${r.ticket_id}`}
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation (admin only) */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete === "bulk"
                ? `Delete ${selected.size} record${selected.size === 1 ? "" : "s"}?`
                : `Delete ${confirmDelete?.ticket_id}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the record{confirmDelete === "bulk" ? "s" : ""} and their WhatsApp
              message logs. An audit-log entry is preserved for compliance. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={doDelete}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              data-testid="delete-confirm"
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
